import type { PropsWithChildren, ReactElement } from 'react';
import * as Sentry from '@sentry/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ResponseError } from '@repo/lib-http-client';

import { logout } from '@/features/auth/authentication';
import { MissingCustomerTokenError } from '@/libs/partner-api-customer-client.service';

const LOGIN_PATH = '/login';

function isSessionEnded(error: unknown): boolean {
  return (
    error instanceof MissingCustomerTokenError ||
    (error instanceof ResponseError && error.statusCode === 401)
  );
}

const VALIDATION_STATUS_CODES = [400, 422];

function isValidationError(error: unknown): boolean {
  return error instanceof ResponseError && VALIDATION_STATUS_CODES.includes(error.statusCode);
}

function endSession(): void {
  logout();

  if (window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query): void => {
        if (isSessionEnded(error)) {
          endSession();

          return;
        }

        Sentry.captureException(error, {
          extra: { queryKey: query.queryKey },
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation): void => {
        if (isSessionEnded(error)) {
          endSession();

          return;
        }

        if (isValidationError(error)) {
          return;
        }

        Sentry.captureException(error, {
          extra: { mutationKey: mutation.options.mutationKey },
        });
      },
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => !isSessionEnded(error) && failureCount < 1,
        staleTime: 30_000,
        gcTime: 0,
      },
    },
  });
}

const queryClient = createQueryClient();

export function QueryProvider({ children }: PropsWithChildren): ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
